import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && typeof detail === 'string' && detail.startsWith('You have view-only access')) {
      toast({ title: 'View only', description: detail, variant: 'destructive' });
    }
    return Promise.reject(error);
  }
);

// Employees
export const getEmployees = (activeOnly = false) => 
  api.get(`/api/employees${activeOnly ? '?active_only=true' : ''}`);

export const getEmployee = (id) => 
  api.get(`/api/employees/${id}`);

export const createEmployee = (data, confirmDuplicate = false) =>
  api.post(`/api/employees${confirmDuplicate ? '?confirm_duplicate=true' : ''}`, data);

export const updateEmployee = (id, data) => 
  api.put(`/api/employees/${id}`, data);

export const deleteEmployee = (id, permanent = false) => 
  api.delete(`/api/employees/${id}${permanent ? '?permanent=true' : ''}`);

export const reinstateEmployee = (id, reinstatement_date) =>
  api.post(`/api/employees/${id}/reinstate`, { reinstatement_date });

// Portal Access
export const getPortalStatus = (employeeId) =>
  api.get(`/api/employees/${employeeId}/portal-status`);

export const togglePortalAccess = (employeeId, data) =>
  api.post(`/api/employees/${employeeId}/toggle-portal`, data);


// Leaves
export const getLeaves = (employeeId = null, year = null) => {
  let url = '/api/leaves';
  const params = [];
  if (employeeId) params.push(`employee_id=${employeeId}`);
  if (year) params.push(`year=${year}`);
  if (params.length > 0) url += '?' + params.join('&');
  return api.get(url);
};

export const createLeave = (data, confirmDuplicate = false, confirmLop = false) => {
  const params = [];
  if (confirmDuplicate) params.push('confirm_duplicate=true');
  if (confirmLop) params.push('confirm_lop=true');
  const qs = params.length ? `?${params.join('&')}` : '';
  return api.post(`/api/leaves${qs}`, data);
};

export const updateLeave = (id, data) =>
  api.put(`/api/leaves/${id}`, data);

export const getLeaveSplitPreview = (employeeId, startDate, endDate, leaveType = 'annual', leaveId = null) =>
  api.get(`/api/leaves/split-preview`, {
    params: { employee_id: employeeId, start_date: startDate, end_date: endDate, leave_type: leaveType, ...(leaveId ? { leave_id: leaveId } : {}) },
  });

export const sendMeetingEmail = (employeeId, { subject, message } = {}) =>
  api.post(`/api/employees/${employeeId}/send-meeting-email`, { subject, message });

export const getLeaveBalance = (employeeId, year = null) => {
  let url = `/api/leaves/balance/${employeeId}`;
  if (year) url += `?year=${year}`;
  return api.get(url);
};

export const getCurrentLeaves = () => 
  api.get('/api/leaves/current');

export const getUpcomingLeaves = (days = 30) => 
  api.get(`/api/leaves/upcoming?days=${days}`);

export const deleteLeave = (id) => 
  api.delete(`/api/leaves/${id}`);

// Data Cleanup — broken leave records
export const getBrokenLeaves = () =>
  api.get('/api/leaves/broken');

export const autoFixBrokenLeave = (id) =>
  api.post(`/api/leaves/broken/${id}/auto-fix`);

// Advance leave-salary recovery on early exit (resign/terminate)
export const getAdvanceLeaveRecovery = (employeeId, lastWorkingDate) =>
  api.get(`/api/leaves/advance-recovery/${employeeId}`, {
    params: lastWorkingDate ? { last_working_date: lastWorkingDate } : {},
  });

export const getAdvanceRecoveryLog = (employeeId = null) =>
  api.get('/api/leaves/advance-recovery-log', {
    params: employeeId ? { employee_id: employeeId } : {},
  });

// Central company profile (name shown on all documents)
export const getCompanyProfile = () => api.get('/api/company-profile');
export const updateCompanyProfile = (name) => api.put('/api/company-profile', { name });

// Leave Balance Warnings
export const getLeaveBalanceWarnings = () =>
  api.get('/api/leaves/balance-warnings');

// Payroll
export const getPayrollHistory = (limit = 12) =>
  api.get(`/api/payroll/history?limit=${limit}`);

export const getCurrentPayroll = () =>
  api.get('/api/payroll/current');

export const createPayrollSnapshot = () =>
  api.post('/api/payroll/snapshot');

export const deletePayrollSnapshot = (id) =>
  api.delete(`/api/payroll/snapshot/${id}`);

// Dashboard
export const getDashboardStats = () => 
  api.get('/api/dashboard/stats');

export const getExpiringDocuments = (days = 90) => 
  api.get(`/api/dashboard/expiring-documents?days=${days}`);

export const getExpiredDocuments = () =>
  api.get('/api/dashboard/expired-documents');

export const getUpcomingBirthdays = (days = 30) =>
  api.get(`/api/dashboard/upcoming-birthdays?days=${days}`);

export const calculateEOSB = (employeeId, calculationDate = null) => {
  let url = `/api/dashboard/eosb/${employeeId}`;
  if (calculationDate) url += `?calculation_date=${calculationDate}`;
  return api.get(url);
};

// Public Holiday Credits
export const addPHCredit = (data) =>
  api.post('/api/ph/credit', data);

export const getEmployeePHCredits = (employeeId) =>
  api.get(`/api/ph/employee/${employeeId}`);

export const getEmployeePHBalance = (employeeId) =>
  api.get(`/api/ph/employee/${employeeId}/balance`);

export const usePHCredit = (phId) =>
  api.post(`/api/ph/use/${phId}`);

export const deletePHCredit = (phId) =>
  api.delete(`/api/ph/${phId}`);

export const setPHCreditExpiry = (phId, expiryDate) =>
  api.put(`/api/ph/${phId}/expiry`, { expiry_date: expiryDate });

export const getAllPHCredits = () =>
  api.get('/api/ph/all');

export const getExpiringPHCredits = (days = 30) =>
  api.get(`/api/ph/expiring?days=${days}`);

// Document clash check for leave
export const checkDocumentClash = (employeeId, startDate, endDate) =>
  api.post(`/api/leaves/check-document-clash?employee_id=${employeeId}&start_date=${startDate}&end_date=${endDate}`);

// Partial Paid Override
export const previewPartialPaid = (leaveId) =>
  api.get(`/api/leaves/${leaveId}/partial-paid-preview`);

export const overrideToPartialPaid = (leaveId) =>
  api.post(`/api/leaves/${leaveId}/override-partial-paid`);

// Probation Payment Authorization (HR override for probation-converted leaves)
export const previewProbationPayment = (leaveId) =>
  api.get(`/api/leaves/${leaveId}/probation-payment-preview`);

export const authorizeProbationPayment = (leaveId, authorizedBy = 'HR') =>
  api.post(`/api/leaves/${leaveId}/authorize-probation-payment`, { authorized_by: authorizedBy });

// All leave balances for report
export const getAllLeaveBalances = () =>
  api.get('/api/leaves/all-balances');

// Advance Leave Payments
export const getAdvancePayments = (employeeId) =>
  api.get(`/api/leaves/advance-payments/${employeeId}`);

export const createAdvancePayment = (employeeId, data) =>
  api.post(`/api/leaves/advance-payments/${employeeId}`, data);

export const deleteAdvancePayment = (employeeId, year) =>
  api.delete(`/api/leaves/advance-payments/${employeeId}/${year}`);

// Leave Audit
export const runLeaveAudit = () => api.get('/api/leaves/audit');

// Leave Approval Workflow (email-based)
export const getLeaveApprovalSettings = () => api.get('/api/leave-approvals/settings');
export const setLeaveApprovalSettings = (settings) =>
  api.put('/api/leave-approvals/settings',
    typeof settings === 'string' ? { default_approver_email: settings } : settings);
export const resolveLeaveApprover = (department) =>
  api.get(`/api/leave-approvals/resolve-approver?department=${encodeURIComponent(department || '')}`);
export const listLeaveApprovals = (status) =>
  api.get(`/api/leave-approvals${status ? `?status=${status}` : ''}`);
export const getLeaveApprovalsSummary = () => api.get('/api/leave-approvals/summary');
export const createLeaveApprovalRequest = (data) =>
  api.post('/api/leave-approvals/request', data);
export const resendLeaveApproval = (id) => api.post(`/api/leave-approvals/${id}/resend`);
export const cancelLeaveApproval = (id) => api.delete(`/api/leave-approvals/${id}`);

export default api;
